import { useApolloClient } from '@apollo/client';
import { Heading, Spinner, Stack, Text } from '@sixfold/common-ui';
import { isNil } from '@sixfold/typed-primitives';
import React, { useEffect, useMemo, useState } from 'react';

import { CompanyGroupCompany, CompanyGroupGraph, CompanyGroupLink, CompanyGroupPartner } from './company_group_graph';
import { ErrorPlaceholder } from '../../components/error_placeholder';
import { CompanyGroupPartnerRelationship, fetchCompanyGroupPartnerRelationships } from '../fetch_relationships';

interface Props {
  companies: CompanyGroupCompany[];
  links: CompanyGroupLink[];
}

type LoadResult = { relationships: CompanyGroupPartnerRelationship[] } | { error: Error };

export const CompanyGroupNetwork: React.FC<Props> = ({ companies, links }) => {
  const apolloClient = useApolloClient();
  const [loaded, setLoaded] = useState<{ memberIds: string[]; result: LoadResult } | undefined>(undefined);
  const memberIds = useMemo(() => companies.map(({ id }) => id), [companies]);

  useEffect(() => {
    let cancelled = false;
    void fetchCompanyGroupPartnerRelationships(apolloClient, memberIds)
      .then((relationships): LoadResult => ({ relationships }))
      .catch((error: Error): LoadResult => ({ error }))
      .then((result) => {
        if (!cancelled) {
          setLoaded({ memberIds, result });
        }
      });
    return () => {
      cancelled = true;
    };
  }, [apolloClient, memberIds]);

  const result = loaded?.memberIds === memberIds ? loaded.result : undefined;
  const relationships = useMemo(
    () => (result !== undefined && 'relationships' in result ? result.relationships : []),
    [result],
  );
  const partners = useMemo(() => getDirectPartners(relationships), [relationships]);

  return (
    <>
      <Stack direction="column" gap="small">
        <Heading size="small">Network</Heading>
        <NetworkSummary result={result} companyCount={companies.length} />
      </Stack>
      <CompanyGroupGraph
        key={`${links.map(({ id }) => id).join()}:${partners.length}`}
        companies={companies}
        links={links}
        partners={partners}
      />
    </>
  );
};

const NetworkSummary: React.FC<{ result: LoadResult | undefined; companyCount: number }> = ({
  result,
  companyCount,
}) => {
  if (isNil(result)) {
    return <Spinner />;
  }

  if ('error' in result) {
    return <ErrorPlaceholder error={result.error} />;
  }

  const subcontractors = new Set(getDirectPartners(result.relationships).map(({ partnerId }) => partnerId)).size;

  return (
    <Text size="small">
      {companyCount} {companyCount === 1 ? 'company' : 'companies'} in the company group, sharing {subcontractors}{' '}
      {subcontractors === 1 ? 'subcontractor' : 'subcontractors'}
    </Text>
  );
};

function getDirectPartners(relationships: CompanyGroupPartnerRelationship[]): CompanyGroupPartner[] {
  return relationships
    .filter(({ isCompanyGroupCopy }) => !isCompanyGroupCopy)
    .map((relationship) => ({
      partnerId: relationship.sourceCompany.company_id,
      memberId: relationship.targetCompany.company_id,
    }));
}
